import { useUser } from '../../context/UserContext';
import { useChat } from './ChatContext';
import { useChatChannels, ChatChannel } from './useChatChannels';
import { useDmParticipants, DmParticipant } from './useDmParticipants';
import { useTeamUsers, TeamUser } from './useTeamUsers';
import { Loader2 } from 'lucide-react';
import { useEffect, useState, useRef, useMemo } from 'react';
import { supabase } from '../../lib/supabase';
import CreateChannelModal from './CreateChannelModal';
import { useCoarsePointer } from './useCoarsePointer';
import { Avatar, Badge, Icon, IconButton, Popover } from '../ui';

const OWLBERT_USER_ID = 'a0000000-0000-0000-0000-000000000001';

function UserAvatar({
  participant,
  size = 20,
}: {
  participant: DmParticipant;
  size?: number;
}) {
  // Owlbert gets the owl emoji
  if (participant.id === OWLBERT_USER_ID) {
    return (
      <span
        className="flex items-center justify-center flex-shrink-0"
        style={{ width: size, height: size }}
      >
        <span style={{ fontSize: size <= 20 ? '12px' : '14px' }}>🦉</span>
      </span>
    );
  }

  return <Avatar name={participant.name} src={participant.avatar} size={size} />;
}

function ChannelItem({
  channel,
  isActive,
  onClick,
  dmParticipant,
}: {
  channel: ChatChannel;
  isActive: boolean;
  onClick: () => void;
  dmParticipant?: DmParticipant;
}) {
  const touch = useCoarsePointer();
  const hasUnread = channel.unread_count > 0;
  const isDm = channel.type === 'direct';
  const displayName = isDm && dmParticipant
    ? dmParticipant.name.split(' ')[0] // First name only for DMs
    : channel.name;

  return (
    <button
      onClick={onClick}
      className="hover-wash pressable w-full flex items-center text-left"
      style={{
        gap: '9px',
        // touch: ~44px rows so a thumb hits the channel it means to
        padding: touch ? '11px 10px' : '7px 10px',
        border: 'none',
        borderRadius: 'var(--radius-sm)',
        background: isActive ? 'var(--surface-active)' : 'transparent',
        color: isActive || hasUnread ? 'var(--text-body)' : 'var(--text-muted)',
        fontFamily: 'var(--font-ui)',
        fontSize: 'var(--text-sm)',
        fontWeight: (hasUnread
          ? 'var(--weight-semibold)'
          : 'var(--weight-regular)') as React.CSSProperties['fontWeight'],
        cursor: 'pointer',
        transition: 'background var(--dur-base) var(--ease-out)',
      }}
    >
      {/* Channel glyph / DM avatar */}
      {isDm && dmParticipant ? (
        <UserAvatar participant={dmParticipant} size={20} />
      ) : (
        <span
          className="flex-shrink-0 flex items-center justify-center"
          style={{ width: '14px', color: 'var(--text-faint)' }}
        >
          {channel.type === 'client' ? (
            <Icon name="building-2" size={13} />
          ) : (
            '#'
          )}
        </span>
      )}

      {/* Name */}
      <span className="truncate flex-1">{displayName}</span>

      {/* Unread badge */}
      {hasUnread && (
        <Badge
          tone="accent"
          count={channel.unread_count > 99 ? '99+' : channel.unread_count}
        />
      )}
    </button>
  );
}

function NewDmDropdown({
  teamUsers,
  loadingUsers,
  onSelectUser,
  onClose,
  anchorEl,
}: {
  teamUsers: TeamUser[];
  loadingUsers: boolean;
  onSelectUser: (user: TeamUser) => void;
  onClose: () => void;
  anchorEl: HTMLElement | null;
}) {
  const [search, setSearch] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus search input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return teamUsers;
    const q = search.toLowerCase();
    return teamUsers.filter(
      u =>
        u.name.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q)
    );
  }, [teamUsers, search]);

  return (
    <Popover
      open
      anchorEl={anchorEl}
      onClose={onClose}
      matchWidth
      offset={2}
      style={{ padding: 0, overflow: 'hidden' }}
    >
      {/* Search */}
      <div
        className="flex items-center gap-1.5 px-2 py-1.5"
        style={{ borderBottom: '1px solid var(--border-hairline)' }}
      >
        <Icon name="search" size={12} style={{ color: 'var(--text-faint)', flexShrink: 0 }} />
        <input
          ref={inputRef}
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Find a user..."
          className="flex-1 bg-transparent outline-none"
          style={{
            fontFamily: 'var(--font-ui)',
            fontSize: 'var(--text-xs)',
            color: 'var(--text-body)',
          }}
        />
        <button
          onClick={onClose}
          className="hover-wash pressable p-0.5"
          style={{
            border: 'none',
            background: 'transparent',
            borderRadius: 'var(--radius-xs)',
            color: 'var(--text-faint)',
            cursor: 'pointer',
          }}
          title="Close"
        >
          <Icon name="x" size={12} />
        </button>
      </div>

      {/* Users list */}
      <div className="max-h-[200px] overflow-y-auto chat-scrollbar" style={{ padding: '4px' }}>
        {loadingUsers ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="w-4 h-4 animate-spin" style={{ color: 'var(--text-faint)' }} />
          </div>
        ) : filtered.length === 0 ? (
          <p
            className="text-center py-3"
            style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}
          >
            No users found
          </p>
        ) : (
          filtered.map(u => (
            <button
              key={u.id}
              onClick={() => onSelectUser(u)}
              className="hover-wash pressable w-full flex items-center text-left"
              style={{
                gap: '9px',
                padding: '6px 8px',
                border: 'none',
                borderRadius: 'var(--radius-sm)',
                background: 'transparent',
                cursor: 'pointer',
              }}
            >
              <UserAvatar
                participant={{
                  id: u.id,
                  name: u.name,
                  avatar: u.avatar,
                }}
                size={20}
              />
              <div className="min-w-0 flex-1">
                <span
                  className="truncate block"
                  style={{
                    fontSize: 'var(--text-xs)',
                    fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'],
                    color: 'var(--text-body)',
                  }}
                >
                  {u.name}
                </span>
              </div>
            </button>
          ))
        )}
      </div>
    </Popover>
  );
}

export default function ChannelList() {
  const { user } = useUser();
  const { selectedChannelId, setSelectedChannelId, setTotalUnreadCount, triggerChannelRefresh } =
    useChat();
  const { channels, loading, totalUnreadCount } = useChatChannels();
  const dmParticipants = useDmParticipants(channels);
  const { users: teamUsers, loading: loadingUsers } = useTeamUsers();
  const [showDmPicker, setShowDmPicker] = useState(false);
  const [creatingDm, setCreatingDm] = useState(false);
  const [showCreateChannel, setShowCreateChannel] = useState(false);
  const dmSectionRef = useRef<HTMLDivElement>(null);
  // The 20px "+" buttons are fine for a mouse, too small for a thumb
  const touch = useCoarsePointer();
  const plusStyle: React.CSSProperties = touch
    ? { width: '36px', height: '36px', marginRight: '-8px' }
    : { width: '20px', height: '20px' };

  // Sync unread count to context
  useEffect(() => {
    setTotalUnreadCount(totalUnreadCount);
  }, [totalUnreadCount, setTotalUnreadCount]);

  // Separate channels by type
  const teamChannels = useMemo(
    () => channels.filter(ch => ch.type !== 'direct' && ch.type !== 'client'),
    [channels]
  );
  const clientChannels = useMemo(
    () => channels.filter(ch => ch.type === 'client'),
    [channels]
  );
  const dmChannels = useMemo(
    () => channels.filter(ch => ch.type === 'direct'),
    [channels]
  );

  const handleCreateOrSelectDm = async (targetUser: TeamUser) => {
    if (!user || creatingDm) return;

    // Check if DM already exists (locally)
    const existingDm = dmChannels.find(
      ch => dmParticipants.get(ch.id)?.id === targetUser.id
    );

    if (existingDm) {
      setSelectedChannelId(existingDm.id);
      setShowDmPicker(false);
      return;
    }

    setCreatingDm(true);
    try {
      // Create new DM channel
      if (!user.agency_id) throw new Error('Your account has no agency');
      const channelName = `${user.name}, ${targetUser.name}`;
      const { data: newChannel, error: channelError } = await supabase
        .from('chat_channels')
        .insert({
          name: channelName,
          type: 'direct',
          agency_id: user.agency_id,
          created_by: user.id,
        })
        .select()
        .single();

      if (channelError || !newChannel) {
        console.error('Error creating DM channel:', channelError);
        return;
      }

      // Add both users as members
      const { error: memberError } = await supabase
        .from('chat_channel_members')
        .insert([
          { channel_id: newChannel.id, user_id: user.id },
          { channel_id: newChannel.id, user_id: targetUser.id },
        ]);

      if (memberError) {
        console.error('Error adding DM members:', memberError);
        return;
      }

      // Trigger refresh in all useChatChannels instances, then select
      triggerChannelRefresh();

      // Small delay to let the refetch start before selecting
      setTimeout(() => {
        setSelectedChannelId(newChannel.id);
      }, 100);
    } catch (err) {
      console.error('Error creating DM:', err);
    } finally {
      setCreatingDm(false);
      setShowDmPicker(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--text-faint)' }} />
      </div>
    );
  }

  if (channels.length === 0 && !showDmPicker) {
    return (
      <div className="flex flex-col items-center justify-center h-full px-4 text-center">
        <Icon
          name="messages-square"
          size={28}
          className="mb-2"
          style={{ color: 'var(--text-faint)', opacity: 0.6 }}
        />
        <p style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
          No channels yet
        </p>
      </div>
    );
  }

  const groupHeaderStyle: React.CSSProperties = {
    padding: '0 10px',
    marginBottom: '5px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  };

  return (
    <div className="flex flex-col h-full">
      <div
        className="flex-1 overflow-y-auto chat-scrollbar"
        style={{ padding: '12px 8px' }}
      >
        {/* ── CHANNELS ── */}
        <div style={{ marginBottom: '14px' }}>
          <div style={groupHeaderStyle}>
            <span className="t-eyebrow">Channels</span>
            <IconButton
              icon="plus"
              size="sm"
              label="New channel"
              onClick={() => setShowCreateChannel(true)}
              style={plusStyle}
            />
          </div>

          {teamChannels.map(channel => (
            <ChannelItem
              key={channel.id}
              channel={channel}
              isActive={channel.id === selectedChannelId}
              onClick={() => setSelectedChannelId(channel.id)}
            />
          ))}
        </div>

        {/* ── CLIENTS ── */}
        {clientChannels.length > 0 && (
          <div style={{ marginBottom: '14px' }}>
            <div style={groupHeaderStyle}>
              <span className="t-eyebrow">Clients</span>
            </div>

            {clientChannels.map(channel => (
              <ChannelItem
                key={channel.id}
                channel={channel}
                isActive={channel.id === selectedChannelId}
                onClick={() => setSelectedChannelId(channel.id)}
              />
            ))}
          </div>
        )}

        {/* ── DIRECT ── */}
        <div ref={dmSectionRef} className="relative">
          <div style={groupHeaderStyle}>
            <span className="t-eyebrow">Direct</span>
            <IconButton
              icon="plus"
              size="sm"
              label="New direct message"
              onClick={() => setShowDmPicker(prev => !prev)}
              disabled={creatingDm}
              style={plusStyle}
            />
          </div>

          {/* DM Picker Dropdown */}
          {showDmPicker && (
            <NewDmDropdown
              teamUsers={teamUsers}
              loadingUsers={loadingUsers}
              onSelectUser={handleCreateOrSelectDm}
              onClose={() => setShowDmPicker(false)}
              anchorEl={dmSectionRef.current}
            />
          )}
        </div>

        {dmChannels.length > 0 ? (
          dmChannels.map(channel => (
            <ChannelItem
              key={channel.id}
              channel={channel}
              isActive={channel.id === selectedChannelId}
              onClick={() => setSelectedChannelId(channel.id)}
              dmParticipant={dmParticipants.get(channel.id)}
            />
          ))
        ) : (
          !showDmPicker && (
            <p
              className="text-center py-2"
              style={{ fontSize: 'var(--text-2xs)', color: 'var(--text-faint)' }}
            >
              No DMs yet
            </p>
          )
        )}
      </div>

      {/* Create Channel Panel */}
      <CreateChannelModal
        isOpen={showCreateChannel}
        onClose={() => setShowCreateChannel(false)}
      />
    </div>
  );
}
